<!--
  GarmentRenderLayer.vue -- real-Artifi-render primary layer for the Store
  Decorations on-garment preview.

  ADR 0065 AMENDMENT (ACCEPTED 2026-08-05): the PRIMARY preview on the
  Decorations page is a real Artifi live render (the actual garment photo for
  the SKU+color with the decoration composited), with the schematic (pebble
  3b) kept as the FALLBACK. This component owns ONLY the real-render layer: it
  resolves an Artifi serve URL for the operator-selected color, or emits
  ``has-render=false`` so the parent (GarmentPreview) shows the schematic.

  This layer RENDERS THE SAME WAY the product-editor Stage does (2026-08-05
  fix). Stage fires a real render on ``variantSku + selectedColor + a
  decoration exists`` and derives its ``view_code`` from the SKU's LIVE Artifi
  views (useArtifiProductViews -> resolveViewCodeForSku). It does NOT gate the
  render on a placement_zone_box, and it does NOT block the render on
  coverage. The earlier version of THIS component added two over-restrictive
  gates Stage never had -- a PlacementZoneBox precondition (which sourced the
  view_code FROM the box, so a fresh DB with no boxes fell back to the
  schematic for EVERY product) and a hard coverage-block. Both are removed;
  the view_code now comes from the SKU's live views exactly like Stage.

  Render gates KEPT (the never-unbounded-3P controls that still matter for the
  Decorations page's many-placement fan-out; SanMar DoS 2026-07-31):

    1. Bounded fan-out -- useArtifiPreviewCache (permanent S3 cache -> warm =
       0 calls; one call per unique (sku,color,view,artwork) at most).
    2. 90% budget gate (KEPT, Jake's decision) -- useArtifiPreviewBudget; over
       budget -> schematic. Fails OPEN below 90% so normal rendering is
       unaffected; protects the page's real fan-out (many placements at once).
    6. Individual staggered per-placement call, source=decorations_page tagged.

  Controls RELAXED to match Stage (info-only, NOT render gates):

    3. supported_color_variants -- useArtifiCoverage is still consulted for the
       supported-color info/footnote (the parent dims an unsupported chip), but
       it no longer HARD-blocks the render and the render no longer holds
       during coverage loading. An unsupported color's Artifi call 404s and
       falls back gracefully -- same as Stage.
    5. PlacementZoneBox -- no longer gates the render and no longer sources the
       view_code. The box, when present, only positions the dashed OVERLAY,
       which is drawn by the PARENT (GarmentPreview), not by this layer.

  data-testid: garment-render-image-<placementId>.
-->
<template>
    <img
        v-if="renderUrl"
        :src="renderUrl"
        :alt="alt"
        class="garment-render-image"
        :data-testid="`garment-render-image-${placementId}`"
        :data-stale="isStale ? 'true' : 'false'"
    />
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useArtifiPreviewCache } from "@/composables/useArtifiPreviewCache";
import { useArtifiCoverage } from "@/composables/useArtifiCoverage";
import { useArtifiProductViews } from "@/composables/useArtifiProductViews";
import { useArtifiPreviewBudget } from "@/composables/useArtifiPreviewBudget";
import { artifiPreviewEligible } from "@/utils/supplier-entitlement";
import { resolveViewCodeForSku } from "@/utils/placementZoneToViewCode";

const props = defineProps({
    // Master supplier SKU (CampaignProduct.product_item_number) -- the coverage
    // + zone-box fetch key and the Artifi render master_sku.
    supplierSku: { type: String, default: null },
    // Supplier identity -- gates Artifi eligibility exactly like the
    // product-editor Stage (artifiPreviewEligible): the KEY decides once the
    // supplier is set (supplierId non-null); the name answers only for a
    // product with no supplier at all.
    supplierName: { type: String, default: null },
    supplierKey: { type: String, default: null },
    supplierId: { type: [Number, String], default: null },
    // The OPERATOR-SELECTED color (SanMar label). Rendered per SKU+color+view
    // -- render the color the operator is looking at (Jake's decision #1).
    colorName: { type: String, default: null },
    // The placement's zone slug (e.g. "left_chest"). Resolved to the Artifi
    // view code against the SKU's LIVE Artifi views (resolveViewCodeForSku),
    // exactly like the product-editor Stage -- NOT against a PlacementZoneBox.
    zoneSlug: { type: String, default: null },
    // The artwork bound to this placement's variant (null -> bare render).
    artworkFileId: { type: String, default: null },
    placementId: { type: [Number, String], default: "x" },
});

const emit = defineEmits(["has-render", "loading", "warnings"]);

// --- Eligibility gate: only suppliers whose styles Artifi carries (SanMar,
// S&S) have coverage. Anything else feeds the composables a null SKU so their
// watchers skip the fetch (mirrors Stage.artifiSku).
const isArtifiEligible = computed(() =>
    artifiPreviewEligible({
        supplierKey: props.supplierKey,
        supplierKeyResolved: props.supplierId != null,
        supplierName: props.supplierName,
    }),
);
const artifiSku = computed(() =>
    isArtifiEligible.value ? props.supplierSku : null,
);

// --- Control 2: the 90% budget gate. Fetched once, shared page-wide.
const { overBudget, ensure: ensureBudget } = useArtifiPreviewBudget();
ensureBudget();

// --- Control 3 (RELAXED to match Stage): supported_color_variants is consulted
// for the supported-color info the PARENT uses to dim an unsupported chip, but
// it does NOT gate the render here. Stage calls this same composable for its
// coverage footnote yet renders regardless -- an unsupported color's Artifi
// call 404s and falls back gracefully. We keep the instance so the module-level
// cache is warm for the parent (a free shared call), but the render never holds
// on coverage loading and never hard-blocks on isUnsupported.
useArtifiCoverage(artifiSku);

// --- View code, sourced EXACTLY like the product-editor Stage: from the SKU's
// LIVE Artifi views (useArtifiProductViews) fed through resolveViewCodeForSku.
// The zone slug resolves to the matching live view code (with the same legacy
// aliases, e.g. full_back -> "Center Back"); an unmatched slug falls back to
// the SKU's default front view; no live views yet -> null (render holds until
// the views land, then fires -- never on a view the SKU lacks). This is the
// Stage's proven derivation -- NOT a placement_zone_box lookup.
const { views: skuProductViews, loading: viewsLoading } =
    useArtifiProductViews(artifiSku);
const resolvedViewCode = computed(() =>
    resolveViewCodeForSku({
        zoneCode: props.zoneSlug,
        skuViews: skuProductViews.value,
    }),
);

// The gate: mirrors the Stage's render preconditions -- a Artifi-eligible SKU,
// an operator-selected color, a resolvable live view code, and under the 90%
// budget. NO placement_zone_box precondition and NO coverage-block (both were
// gates Stage never had). When ineligible, cacheArgs is null (no Artifi call)
// and the parent shows the schematic.
const renderEligible = computed(() => {
    if (!artifiSku.value) return false; // not Artifi-eligible
    if (!props.colorName) return false;
    if (overBudget.value) return false; // control 2 (KEPT)
    // View code comes from the SKU's live views; hold until they resolve so we
    // never send Artifi a view the SKU lacks (parity with Stage's null view).
    if (!resolvedViewCode.value) return false;
    return true;
});

// "Intends to render" -- renderEligible's preconditions MINUS the resolved
// view-code gate. True the moment the layer is going to TRY a render: a
// Artifi-eligible SKU, an operator-selected color, and under the 90% budget.
// It does NOT wait for the ~0.5s product_views fetch (the source of the view
// code). This drives the LOADING signal only -- it never gates the actual
// Artifi call (that still requires the resolved view code via renderEligible /
// cacheArgs, unchanged), so the render fires exactly when it did before.
const intendsToRender = computed(() => {
    if (!artifiSku.value) return false; // not Artifi-eligible
    if (!props.colorName) return false;
    if (overBudget.value) return false; // control 2 (KEPT)
    return true;
});

// --- Controls 1 + 6: the render itself, via the SHARED preview cache (warm =
// 0 calls) with the decorations_page source tag. Only assembled when eligible.
const cacheArgs = computed(() => {
    if (!renderEligible.value) return null;
    return {
        productSku: props.supplierSku,
        colorSku: props.colorName,
        viewCode: resolvedViewCode.value,
        artworkFileId: props.artworkFileId || null,
        // Backend resolves master_sku + color_name -> Artifi numeric variant
        // SKU (same contract as the Stage).
        variantSku: props.colorName,
        masterSku: props.supplierSku,
        colorName: props.colorName,
        // Control 6: attribute quota burn + arm the server-side 90% gate.
        source: "decorations_page",
    };
});

const {
    url: previewUrl,
    error: previewError,
    loading: previewLoading,
    warnings: previewWarnings,
} = useArtifiPreviewCache(cacheArgs);

// Fallback contract (ADR amendment): (1) real render, (2) render fails / stale
// -> last good (stale) else (3) schematic. We keep the last good URL so a
// transient error keeps showing the previous image (flagged stale) rather than
// snapping to the schematic; a null-from-the-start error emits has-render=false
// and the parent renders the schematic.
const lastGoodUrl = ref(null);
watch(previewUrl, (u) => {
    if (u) lastGoodUrl.value = u;
});

const renderUrl = computed(() => {
    if (!renderEligible.value) return null;
    if (previewUrl.value) return previewUrl.value;
    // Error with a prior good render -> keep the stale image (fallback 2).
    if (previewError.value && lastGoodUrl.value) return lastGoodUrl.value;
    return null;
});

const isStale = computed(
    () => Boolean(previewError.value) && !previewUrl.value && Boolean(renderUrl.value),
);

const alt = computed(
    () => `${props.colorName || "Product"} on-garment preview`,
);

// Tell the parent whether a real render is on screen so it can hide the
// schematic layers (design-philosophy principle 4: one value drives which
// layer shows). Emitted on every change of the resolved URL.
watch(
    renderUrl,
    (u) => emit("has-render", Boolean(u)),
    { immediate: true },
);

// --- Loading affordance (Store Decorations redesign, card oSOHJfg0). Tell the
// parent when a render is IN FLIGHT so it can show a shimmer instead of the
// jarring "schematic sits then snaps to the render" behaviour. It does NOT
// change WHEN or WHETHER a render fires (that logic above is unchanged):
//
//   loading = intendsToRender AND (viewsLoading OR previewLoading)
//
// The signal must cover the ENTIRE resolve window, both phases:
//   1. product_views fetch (~0.5s) -- `viewsLoading` from useArtifiProductViews.
//      This is the phase renderEligible could not see (it needs the resolved
//      view code, which only exists AFTER this fetch), so gating the shimmer on
//      renderEligible left a ~0.5s DEAD WINDOW where the schematic sat with no
//      cue then the shimmer snapped in. Gating on intendsToRender (view-code
//      INDEPENDENT) fires the shimmer immediately instead.
//   2. cache-check + Artifi call (~5s cold) -- `previewLoading` from the shared
//      cache. False synchronously on a HIT (so a warm hit never raises loading
//      -> no flash), false on terminal error (schematic/stale takes over), and
//      re-true whenever cacheArgs changes (a color switch = a new cache key ->
//      shimmer re-appears over the previous color's render, per GarmentPreview).
//
// An INELIGIBLE layer (no SanMar sku / no color / over budget) has
// intendsToRender=false, so it never claims to be loading -- straight to the
// schematic with no shimmer. No `renderUrl` term is needed: the HIT / terminal
// -error cases are already covered by previewLoading being false, and a color
// switch intentionally keeps the shimmer over the stale previous render.
const layerLoading = computed(
    () =>
        Boolean(
            intendsToRender.value &&
                (viewsLoading.value || previewLoading.value),
        ),
);

watch(
    layerLoading,
    (l) => emit("loading", l),
    { immediate: true },
);

// --- Preview warnings (GH #2874). The composite render DROPS a placement it
// cannot draw -- most often two decorations resolving to the same garment view
// -- and reports why as machine codes in the response's ``warnings`` array.
// Nothing in the UI read that array before, so a preview silently missing a
// decoration was indistinguishable from a correct one. That is what was
// reported as decorations "randomly moving".
//
// This layer OWNS the composable, so it is the only place those codes are
// visible, but it is a bare <img> with no chrome. It therefore FORWARDS them
// and the parent (GarmentPreview) decides how to draw the indication -- the
// same division of labour as ``has-render`` and ``loading`` above.
//
// Gated on renderEligible: an ineligible layer never called Artifi, so it has
// nothing to report and must not leave a stale warning on screen from a
// previous eligible state (e.g. after the operator switches to a color that
// puts the page over budget). Codes are forwarded RAW -- translating them to
// human copy is previewWarningCopy's job, and doing it here would duplicate
// that mapping in two places.
const layerWarnings = computed(() => {
    if (!renderEligible.value) return [];
    return previewWarnings.value ?? [];
});

watch(
    layerWarnings,
    (w) => emit("warnings", w),
    { immediate: true, deep: true },
);
</script>

<style scoped>
.garment-render-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>
